import React, { useRef } from 'react';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  FileText,
  ImageIcon,
  Loader2,
  RotateCcw,
  Settings,
  Undo2,
} from 'lucide-react';

const IMAGE_ACCEPT =
  '.jpg,.jpeg,.png,.webp,.gif,.avif,.svg,image/jpeg,image/png,image/webp,image/gif,image/avif,image/svg+xml';

export default function ImportResultStep({
  itemType,
  fileName,
  studioName,
  results,
  attachingIds,
  onAttachImage,
  onRetryFailed,
  retrying,
  onUndo,
  undoing,
  onDownloadReport,
  onImportAnother,
  onImportNextSheet,
  nextSheetLabel,
  onChangeStudio,
}) {
  const inputRef = useRef(null);
  const targetRef = useRef('');
  const isService = itemType === 'service';
  const noun = isService ? 'service' : 'product';
  const nouns = isService ? 'services' : 'products';
  const count = (n) => `${n} ${n === 1 ? noun : nouns}`;

  const imported = results.filter((r) => r.status === 'imported');
  const failed = results.filter((r) => r.status === 'failed');
  const skipped = results.filter((r) => r.status === 'skipped');
  const removed = results.filter((r) => r.status === 'removed');
  const imagesToAttach = imported.filter((r) => !r.payload.imageUrl).length;
  const undone = removed.length > 0 && imported.length === 0;
  const busy = Boolean(retrying) || undoing;

  let heading = `${count(imported.length)} imported successfully`;
  let detail = `${studioName} can find them in Products & Services.`;
  if (undone) {
    heading = `${count(removed.length)} deleted`;
    detail = 'Vendors and categories the import added are still there.';
  } else if (failed.length) {
    heading = `${imported.length} of ${count(results.length)} imported`;
    detail = `${failed.length} couldn't be saved. Retry ${
      failed.length === 1 ? 'it' : 'them'
    }, or download the import report to see why.`;
  }
  if (!undone && skipped.length) {
    detail += ` ${skipped.length} ${
      skipped.length === 1 ? 'was' : 'were'
    } already in Products & Services and skipped.`;
  }

  const pickImage = (resultId) => {
    targetRef.current = resultId;
    inputRef.current?.click();
  };

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (file && targetRef.current) onAttachImage(targetRef.current, file);
    e.target.value = '';
  };

  const statusCell = (result) => {
    if (result.status === 'imported') {
      return (
        <span className="pi-chip pi-chip--ok">
          <CheckCircle2 size={15} aria-hidden="true" /> Imported
        </span>
      );
    }
    if (result.status === 'failed') {
      return (
        <div className="pi-failed">
          <span className="pi-chip pi-chip--error">
            <AlertCircle size={15} aria-hidden="true" /> Failed
          </span>
          <span className="pi-failed-reason">{result.error}</span>
        </div>
      );
    }
    return (
      <div className="pi-failed">
        <span className="pi-chip pi-chip--muted">
          {result.status === 'removed' ? 'Removed' : 'Skipped'}
        </span>
        {result.error && <span className="pi-muted pi-status-note">{result.error}</span>}
      </div>
    );
  };

  return (
    <>
      <div className="pi-result-hero">
        <div
          className={`pi-result-icon${undone ? ' is-neutral' : failed.length ? ' is-partial' : ''}`}
        >
          {undone ? (
            <Undo2 size={30} />
          ) : failed.length ? (
            <AlertTriangle size={30} />
          ) : (
            <Check size={34} strokeWidth={3} />
          )}
        </div>
        <h3>{heading}</h3>
        <p>{detail}</p>
        <div className="pi-card pi-file-chip pi-file-chip--center">
          <FileSpreadsheet className="pi-excel-icon" size={22} aria-hidden="true" />
          <span>{fileName}</span>
        </div>
      </div>

      <div className="pi-stats">
        <div className="pi-stat pi-stat--ok">
          <FileText size={30} aria-hidden="true" />
          <div>
            <strong>{imported.length}</strong>
            <span>Imported</span>
          </div>
        </div>
        <div className="pi-stat pi-stat--error">
          <AlertCircle size={30} aria-hidden="true" />
          <div>
            <strong>{failed.length}</strong>
            <span>Failed</span>
          </div>
        </div>
        <div className="pi-stat pi-stat--info">
          <ImageIcon size={30} aria-hidden="true" />
          <div>
            <strong>{imagesToAttach}</strong>
            <span>Images to attach</span>
          </div>
        </div>
      </div>

      {imagesToAttach > 0 && (
        <div className="pi-banner pi-banner--warning">
          <AlertTriangle size={18} aria-hidden="true" />
          {isService ? 'Services' : 'Products'} imported without photos. Attach the matching image
          files whenever you're ready.
        </div>
      )}

      <section className="pi-card pi-result-card">
        <div className="pi-table-wrap">
          <table className="pi-table pi-result-table">
            <thead>
              <tr>
                <th>{isService ? 'Service' : 'Product'}</th>
                <th>Vendor</th>
                <th>Category</th>
                <th>Import status</th>
                <th>Image</th>
              </tr>
            </thead>
            <tbody>
              {results.map((result) => {
                const attaching = attachingIds.has(result.id);
                const live = result.status === 'imported';
                return (
                  <tr key={result.id}>
                    <td>{result.name}</td>
                    <td>{result.vendor || '—'}</td>
                    <td>{result.category || '—'}</td>
                    <td>{statusCell(result)}</td>
                    <td>
                      {!live ? (
                        '—'
                      ) : attaching ? (
                        <span className="pi-muted">
                          <Loader2 size={15} className="pi-spin" aria-hidden="true" /> Uploading…
                        </span>
                      ) : result.payload.imageUrl ? (
                        <span className="pi-attached">
                          <img src={result.payload.imageUrl} alt="" /> Attached
                        </span>
                      ) : (
                        <div className="pi-failed">
                          <button
                            type="button"
                            className="pi-link"
                            onClick={() => pickImage(result.id)}
                          >
                            Attach image
                          </button>
                          {result.photoError && (
                            <span className="pi-muted pi-status-note">
                              Photo not copied: {result.photoError}
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      {!undone && (
        <div className="pi-card pi-finish-card">
          <div className="pi-finish-icon">
            <Settings size={20} aria-hidden="true" />
          </div>
          <div>
            <strong>Finish setting up these {nouns}</strong>
            <p>
              Attach {noun} images here. The studio can make any other changes in Products &amp;
              Services.
            </p>
          </div>
        </div>
      )}

      <div className="pi-footer">
        <div className="pi-footer-actions pi-footer-actions--start">
          <button type="button" className="pi-btn pi-btn--outline" onClick={onDownloadReport}>
            <Download size={16} aria-hidden="true" /> Download import report
          </button>
          {imported.length > 0 && (
            <button
              type="button"
              className="pi-btn pi-btn--outline pi-btn--danger"
              onClick={onUndo}
              disabled={busy}
            >
              {undoing ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Undoing…
                </>
              ) : (
                <>
                  <Undo2 size={16} aria-hidden="true" /> Undo import
                </>
              )}
            </button>
          )}
        </div>
        <div className="pi-footer-actions">
          {failed.length > 0 && (
            <button
              type="button"
              className="pi-btn pi-btn--outline-accent"
              onClick={onRetryFailed}
              disabled={busy}
            >
              {retrying ? (
                <>
                  <Loader2 size={16} className="pi-spin" aria-hidden="true" /> Retrying{' '}
                  {retrying.done} of {retrying.total}…
                </>
              ) : (
                <>
                  <RotateCcw size={16} aria-hidden="true" /> Retry {failed.length} failed
                </>
              )}
            </button>
          )}
          {onImportNextSheet && (
            <button
              type="button"
              className="pi-btn pi-btn--outline-accent"
              onClick={onImportNextSheet}
              disabled={busy}
            >
              {nextSheetLabel}
            </button>
          )}
          <button
            type="button"
            className="pi-btn pi-btn--outline-accent"
            onClick={onImportAnother}
            disabled={busy}
          >
            Import another file
          </button>
          <button
            type="button"
            className="pi-btn pi-btn--primary pi-btn--lg"
            onClick={onChangeStudio}
            disabled={busy}
          >
            Import for another studio
          </button>
        </div>
      </div>

      <input
        ref={inputRef}
        id="pi-attach-image-input"
        type="file"
        accept={IMAGE_ACCEPT}
        onChange={handleImage}
        hidden
      />
    </>
  );
}
